import { useEffect, useState } from 'react';

import { listBlocks, type BlockManifest } from '../../lib/cmsAdmin';
import { setBlockMetadata, isMetadataLoaded } from '../../cms/editor/blockMeta';

/**
 * Load the block registry's metadata and hand it to the pure editor utilities.
 *
 * ── WHY THE METADATA COMES FROM THE SERVER ───────────────────────────────────────────
 *
 * The portal already has a compiled registry — it is what renders the blocks. It does
 * NOT have the prop schemas, the editor hints or the labels, because a public visitor has
 * no use for them and they would be dead weight in the bundle every visitor downloads.
 * The engine holds those in src/cms/blocks.js, and verify-cms-registry.mjs fails the
 * build if the two halves disagree about any block, its version, its contexts or its
 * child rule. So this fetch cannot introduce a block the renderer does not have.
 *
 * The utilities in cms/editor are deliberately free of both React and the registry, so
 * they can be executed by node in a unit test. The price of that is that somebody has to
 * inject what they know about blocks, and this is that somebody.
 */
export interface BlockMetadataState {
  manifests: BlockManifest[];
  loading: boolean;
  error: string | null;
}

let cache: BlockManifest[] | null = null;

export function useBlockMetadata(): BlockMetadataState {
  const [manifests, setManifests] = useState<BlockManifest[]>(cache ?? []);
  const [loading, setLoading] = useState(!cache);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (cache && isMetadataLoaded()) return;
    let cancelled = false;
    setLoading(true);
    listBlocks()
      .then((blocks) => {
        if (cancelled) return;
        cache = blocks;
        setBlockMetadata(blocks.map((b) => ({
          id: b.id,
          label: b.label,
          version: b.version,
          category: b.category,
          contexts: b.contexts,
          children: b.children,
          critical: b.critical,
          defaultProps: b.defaultProps,
          propsSchema: b.propsSchema,
          editor: b.editor,
        })));
        setManifests(blocks);
      })
      .catch(() => { if (!cancelled) setError('The block registry could not be loaded.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return { manifests, loading, error };
}
